import React, { FunctionComponent, useEffect, useRef } from 'react'
import {
  FieldLabel,
  FieldValue,
  Popover,
  Text,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { LineItem } from '../../../../models/line-item'
import { LineItemInput } from '../LineItemInput'
import { LineItemSplitQuantityError } from '../input-error-message/LineItemSplitQuantityError'
import { LineItemSplitQuantityActionBar } from './LineItemSplitQuantityActionBar'

interface Props {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  lineItem: LineItem
  splitQuantity: number
  inputError: string | null
  onQuantityChange: (value: string) => void
  onQuantityError: (error: string | null) => void
  hasError: boolean
}

export const LineItemSplitQuantityPopover: FunctionComponent<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  lineItem,
  splitQuantity,
  inputError,
  onQuantityChange,
  onQuantityError,
  hasError,
}) => {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Popover
      onClose={onClose}
      actions={
        <LineItemSplitQuantityActionBar
          onCancel={onClose}
          onConfirm={onConfirm}
          isSplitDisabled={splitQuantity === 0 || hasError}
        />
      }
    >
      <Text type="label" tag="p" className="vd-mb3">
        <T _str="Split quantity" _context="Split quantity popover header" />
      </Text>
      <hr className="vd-hr"></hr>
      <div>
        <FieldLabel label={t('How many items would you like to split?')} />
        <FieldValue>
          <LineItemInput
            ref={inputRef}
            className="vd-mb3"
            value={splitQuantity}
            decimalPlaces={3}
            error={inputError}
            onError={onQuantityError}
            onValueChange={onQuantityChange}
            onFocusChange={onQuantityChange}
            onFocus={event => {
              event.target.select()
            }}
            trimInsignificantDigits
          />
        </FieldValue>
      </div>
      <LineItemSplitQuantityError lineItem={lineItem} error={inputError} />
    </Popover>
  )
}
